<template>
  <div class="container-fluid">
    <div class="row align-items-center mb-3">
      <h1 class="col-auto mb-0">NB-NTN Coverage</h1>
      <!-- Hidden file dialog enabled by button -->
      <input 
        type="file" 
        ref="fileInputRef" 
        accept=".kml" 
        style="display: none" 
        @change="handleFileSelection"
      />
      <div class="d-flex align-items-center gap-2 col-auto ms-auto">
        
        <button 
          v-if="auth.isAdmin" 
          type="button" 
          class="btn btn-sm btn-warning"
          :disabled="isUploading"
          @click="triggerFileDialog"
        >
          <span v-if="isUploading" class="spinner-border spinner-border-sm me-1" role="status" />
          {{ isUploading ? 'Uploading...' : 'Upload KML' }}
        </button>

        <button 
          v-if="beamsLoaded" 
          type="button" 
          class="btn btn-sm btn-secondary" 
          @click="showDark = !showDark"
        >
          {{ showDark ? 'Hide Dark Beams' : 'Show Dark Beams' }}
        </button>

      </div>
    </div>
    
    <div v-if="!beamsLoaded" class="text-center my-5">
      <div class="spinner-border text-primary" role="status" />
      <div class="mt-2 text-muted">Loading satellite coverage...</div>
    </div>
    
    <div v-else class="container-fluid" style="max-height: 90vh;">
      <CoverageMap 
        :geojsonData="filteredBeams" 
        :satellites="satellites" 
        :lit="lit"
        :tableSelectedBeam="tableSelectedBeam"
        @beam-selected="updateBeamTable" 
      />
    </div>
    
    <form 
      v-if="auth.isAdmin && beamsLoaded && beamTableData.length > 0" 
      @submit.prevent="handleSubmit" 
      class="mt-4"
    >
      <div class="card card-body border-secondary">
        <div class="row d-flex align-items-center mb-3">
          <h5 class="col-auto mb-0">Edit Beam Status</h5>
          <div class="col-auto ms-auto">
            <button type="button" class="btn btn-sm btn-outline-secondary me-2" @click="listAllBeams">
              List All
            </button>
            <button type="submit" class="btn btn-sm btn-primary">
              Update Status
            </button>
          </div>
        </div>
        
        <div class="table-responsive">
          <BeamTable 
            :data="beamTableData"
            :columns="tableColumns"
            @row-update="handleDataUpdate"
            @row-click="tableSelectBeamOnMap"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint, mapClip, mapClipBounds } from '@/utils/config';
import { apiFetch, uploadFile } from '@/utils/api';

import CoverageMap from '../components/CoverageMapComponent.vue';
import BeamTable from '../components/TableComponent.vue';

const auth = useAuthStore();
const schema = useSchemaStore();

// --- State ---
const beams = ref([]);
const satellites = ref([]);
const lit = ref([]);
const hide = ref([]);
const showDark = ref(false);
const beamsLoaded = ref(false);
const beamStatuses = ref([]);
const beamTableData = ref([]);
const tableSelectedBeam = ref(null);

const networkTypes = computed(() => schema.getEnumValues('NetworkType'));
let originalBeamStatuses = [];

const fileInputRef = ref(null);
const isUploading = ref(false);

const tableColumns = computed(() => [
  { title: 'Beam ID', data: 'id' },
  { title: 'Lit', data: 'lit', editType: 'checkbox', dataId: 'id' },
  { title: 'Show', data: 'show', editType: 'checkbox', dataId: 'id' },
  { title: 'Cell ID', data: 'cellId', editType: 'text', dataId: 'id' },
  { title: 'EARFCN', data: 'earfcn', editType: 'text', dataId: 'id' },
  { 
    title: 'Network',
    data: 'networkType',
    editType: 'select',
    options: networkTypes,
    dataId: 'id',
  },
  { title: 'Operator', data: 'operator', editType: 'text', dataId: 'id' },
]);

// --- Computed ---
const filteredBeams = computed(() => {
  const filtered = beams.value.map(region => ({
    ...region,
    features: region.features.filter(feature => {
      const beamName = feature.properties?.name || feature.id;
      // If not showing dark beams, hide those that are not 'lit'
      if (!showDark.value && !lit.value.includes(beamName)) return false;
      return !hide.value.includes(beamName);
    })
  }));
  return filtered;
});

// --- Methods ---
const getBeams = async () => {
  try {
    const mapQuery = mapClip ? `?clip=${JSON.stringify(mapClipBounds)}` : '';
    const { geojson } = await apiFetch(ApiEndpoint.COVERAGE + `/map${mapQuery}`);
    const { status } = await apiFetch(ApiEndpoint.COVERAGE + '/status');

    if (!Array.isArray(geojson)) {
      throw new Error(`Unable to retrieve GeoJSON values`);
    }
    if (!Array.isArray(status)) {
      throw new Error(`Unable to retrieve beam status values`);
    }

    satellites.value = [];
    let contourCount = 0;
    geojson.forEach(region => {
      if (region.features.length > 0) {
        const feature = region.features[0];
        if (feature.geometry?.type === 'Point') {
          satellites.value.push({
            name: feature.properties?.name || feature.id || '?',
            longitude: feature.geometry.coordinates[0],
          });
          region.features.shift(); // Remove satellite point from beam features
        }
        contourCount += region.features.length;
      }
    });
    beams.value = geojson;
    originalBeamStatuses = JSON.parse(JSON.stringify(status));
    beamStatuses.value = status;
    const litCount = status.reduce((acc, obj) => obj.lit ? acc + 1 : acc, 0);
    console.debug(`Loaded ${contourCount} beam contours (${litCount} lit)`);
    syncMapVisibility();
    beamsLoaded.value = true;
  } catch (err) {
    console.error('Failed to load coverage:', err);
  }
};

const syncMapVisibility = () => {
  lit.value = beamStatuses.value.filter(s => s.lit).map(s => s.id);
  hide.value = beamStatuses.value.filter(s => !s.show).map(s => s.id);
};

const updateBeamTable = (beamInfo) => {
  const { selected, beamName } = beamInfo;
  if (selected) {
    if (!beamTableData.value.some(e => e.id === beamName)) {
      const status = beamStatuses.value.find(e => e.id === beamName);
      if (status) {
        beamTableData.value.push({ ...status });
        beamTableData.value.sort((a, b) => a.id.localeCompare(b.id));
      }
    }
  } else {
    const index = beamTableData.value.findIndex(e => e.id === beamName);
    if (index !== -1) beamTableData.value.splice(index, 1);
  }
};

const handleDataUpdate = (newData) => {
  beamTableData.value = newData;
  newData.forEach(updatedBeam => {
    const index = beamStatuses.value.findIndex(s => s.id === updatedBeam.id);
    if (index !== -1) {
      beamStatuses.value[index] = { ...updatedBeam };
    }
  });
  syncMapVisibility();
};

const tableSelectBeamOnMap = (row) => {
  tableSelectedBeam.value = row.id;
};

const listAllBeams = () => {
  beamTableData.value = [...beamStatuses.value].sort((a, b) => 
    a.id.localeCompare(b.id)
  );
};

const handleSubmit = async () => {
  try {
    // Filter out only the rows that don't match baseline backup
    const payload = beamStatuses.value.reduce((acc, currentBeam) => {
      const original = originalBeamStatuses.find(o => o.id === currentBeam.id);
      // If it's a completely new beam record, pass the whole object
      if (!original) {
        acc.push({ ...currentBeam });
        return acc;
      }
      // Track only what changed
      const changes = {};
      for (const k in currentBeam) {
        if (k in original && currentBeam[k] !== original[k]) {
          changes[k] = currentBeam[k];
        }
      }
      // Only push to payload if at least one property changed
      if (Object.keys(changes).length > 0) {
        acc.push({
          id: currentBeam.id, // Primary key is always included
          ...changes
        });
      }
      return acc;
    }, []);
    if (payload.length === 0) {
      alert(`No status changes detected`);
      return;
    }
    const result = await apiFetch(ApiEndpoint.COVERAGE, {
      method: 'PATCH',
      body: payload,
    });
    originalBeamStatuses = JSON.parse(JSON.stringify(beamStatuses.value));
    confirm(`Beam status updated successfully for ${payload.length} beams`);
  } catch (err) {
    alert(`Update failed: ${err.message}`);
  }
};

const triggerFileDialog = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const handleFileSelection = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  isUploading.value = true;

  try {
    const endpoint = ApiEndpoint.COVERAGE + '/kml';
    const result = await uploadFile(endpoint, file, { tag: 'kmlFile' });

    alert(`Success: Ingested ${result.updated} beam contours` +
      ` on satellites: ${result.satellites.join(', ')}`);
    
    // Refresh the map view automatically to reveal the new coordinates
    beamsLoaded.value = false;
    await getBeams();

  } catch (err) {
    console.error('KML Upload crash:', err);
    alert(`KML Ingestion failed: ${err.message}`);
  } finally {
    isUploading.value = false;
    if (fileInputRef.value) fileInputRef.value.value = ''; // Reset input to allow re-uploads
  }
};

onMounted(() => {
  getBeams();
});
</script>
